import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/core';
import { RestMessage } from '@app/core/types';
import { CodeVerificationResponse, SendVerificationCodeResponse } from '@app/core/types/user';
import { MFATypes } from '@projects/agent/src/app/models/userprofile';
import { MFAFlags } from '@projects/shared/src/app/enums/MFAFlags';
import { ToastService } from '@projects/shared/src/app/services/toast/toast.service';
import { formatTimeLeft } from '@projects/shared/src/app/utils/helper';

@Component({
  selector: 'app-sms-verification',
  templateUrl: './sms-verification.component.html',
  styleUrl: './sms-verification.component.scss',
})
export class SMSVerificationComponent {
  @Input() userId?: string | null;
  @Input() phoneNumber?: string | null;
  @Input() isLoginFlow = true;
  @Input() imagePath = '';

  @Output() smsVerified = new EventEmitter<boolean>();

  code = '';
  smsCount = 0;
  timeLeft = 0;
  private timerInterval: any;
  isSMSSent = false;
  isError = false;
  verified: any;

  constructor(
    private authService: AuthenticationService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    if (this.isLoginFlow) {
      this.userId = this.route.snapshot.queryParamMap.get('id');
      this.phoneNumber = this.route.snapshot.queryParamMap.get('phoneNumber');
      this.verified = this.route.snapshot.queryParamMap.get('verified');
      this.sendCodeSMS();
    }
  }

  get displayTimeLeft(): string {
    return formatTimeLeft(this.timeLeft);
  }

  /**
   * Sends verification code to an email address
   */
  sendCodeSMS() {
    this.validateUserId();

    if (this.smsCount >= 3) {
      this.toastService.warning('You have reached the limit to send the SMS. Please try again later');
      return;
    }

    this.isSMSSent = false;
    this.authService.sendMfaVerificationCode({ UserId: this.userId, MfaChannel: MFATypes.SMS }).subscribe({
      next: (response: SendVerificationCodeResponse) => {
        this.toastService.success('Please check your SMS for the confirmation code.');
        this.isSMSSent = true;
        this.smsCount++;
        this.timeLeft = response.ExpireInSeconds;
        this.startResendTimer();
      },
      error: (err: any) => {
        this.isSMSSent = false;
        this.toastService.error('Failed to send the SMS verification code. Please try again later.');
      },
    });
  }

  /**
   * Verifies code for Email verification
   * @param code
   * @returns
   */
  verifyCode(code: any) {
    this.validateUserId();

    if (code === undefined || code === '') {
      this.toastService.error("Sorry, we don't recognize this verification code.");
      this.isError = true;
      this.code = '';
      return;
    }

    const codeVerification = {
      UserId: this.userId || '',
      Code: code,
      IsLoginFlow: this.isLoginFlow,
      MfaChannel: MFATypes.SMS,
    };

    this.authService.verifyCode(codeVerification).subscribe((response: CodeVerificationResponse) => {
      if (response?.Success) {
        this.toastService.success('SMS verified successfully.');
        this.code = '';

        if (this.isLoginFlow) {
          this.authService.configureMFAAuthData(true, MFAFlags.SMS);
          this.router.navigateByUrl('login');
        } else {
          this.smsVerified.emit(true);
        }
      } else {
        this.authService.handleVerificationError(response.Error);

        if (this.isLoginFlow) this.authService.configureMFAAuthData(false, MFAFlags.SMS);
      }
    });
  }

  startResendTimer() {
    if (this.timeLeft <= 0) return;

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
      } else {
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  /**
   * Back to home
   */
  onBack() {
    this.authService.onLogOut();
  }

  private validateUserId(): void {
    if (!this.userId) {
      if (this.isLoginFlow) {
        this.toastService.error('User is not logged in.');
        this.router.navigateByUrl('/login');
      } else {
        this.toastService.error('User is not provided.');
        this.smsVerified.emit(false);
      }

      return;
    }
  }
}
